import * as THREE from 'three';

/**
 * Read a WebGLRenderTarget's pixels back to the CPU and return a webp data URL.
 * WebGL pixel rows are bottom-up; this function flips them to top-down for canvas.
 */
export function readRTToDataURL(
  gl: THREE.WebGLRenderer,
  rt: THREE.WebGLRenderTarget,
  pixelBufferRef: { current: Uint8Array | null },
): string {
  const { width: w, height: h } = rt;
  const needed = w * h * 4;
  if (!pixelBufferRef.current || pixelBufferRef.current.length !== needed)
    pixelBufferRef.current = new Uint8Array(needed);
  const pixels = pixelBufferRef.current;
  gl.readRenderTargetPixels(rt, 0, 0, w, h, pixels);
  const offscreen = document.createElement('canvas');
  offscreen.width = w;
  offscreen.height = h;
  const ctx = offscreen.getContext('2d')!;
  const imgData = ctx.createImageData(w, h);
  // Flip Y: WebGL reads rows bottom-up, ImageData is top-down.
  for (let row = 0; row < h; row++) {
    const src = (h - 1 - row) * w * 4;
    imgData.data.set(pixels.subarray(src, src + w * 4), row * w * 4);
  }
  ctx.putImageData(imgData, 0, 0);
  return offscreen.toDataURL('image/webp', 0.85);
}

/**
 * Upload a webp/png data URL into a WebGLRenderTarget via the copy shader.
 */
export function loadDataURLToRT(
  dataUrl: string,
  rt: THREE.WebGLRenderTarget,
  gl: THREE.WebGLRenderer,
  copyMaterial: THREE.ShaderMaterial,
  copyScene: THREE.Scene,
  orthoCamera: THREE.Camera,
): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const offscreen = document.createElement('canvas');
      offscreen.width = rt.width;
      offscreen.height = rt.height;
      offscreen.getContext('2d')!.drawImage(img, 0, 0);
      const tex = new THREE.CanvasTexture(offscreen);
      // The data URL was produced by readRTToDataURL from an sRGB render target,
      // so its pixel values are already sRGB-encoded.  Tag the texture as sRGB
      // so Three.js linearizes them on read; the copy shader then writes them to
      // the target sRGB RT which re-encodes on output — a clean round-trip.
      // Without this, the GPU double-encodes the sRGB values, making all colors
      // lighter/brighter after every undo restore.
      tex.colorSpace = THREE.SRGBColorSpace;
      (copyMaterial as any).uniforms.uTex.value = tex;
      gl.setRenderTarget(rt);
      gl.render(copyScene, orthoCamera);
      gl.setRenderTarget(null);
      tex.dispose();
      resolve();
    };
    img.onerror = () => resolve();
    img.src = dataUrl;
  });
}
